import { supabase } from "@/integrations/supabase/client";
import { extractSyncBolS3Error, formatSyncBolS3Error } from "@/lib/sync-bol-s3-error";

export type OrderDocTable = "order_bol_images" | "order_tender_images";

export interface OrderDocKeyRow {
  /** The S3 key persisted on the document row at upload time (preferred). */
  s3_key?: string | null;
  /** The ORIGINAL uploaded file name — this is what `s3_sync_log.source_path` stores. */
  file_name?: string | null;
  /** Randomized local storage path (`bol/<order>/<ts>-<rand>.ext`). Never matches the sync log. */
  image_path?: string | null;
}

/**
 * Single source of truth for resolving the NetSuite-facing S3 key(s) of an
 * order document.
 *
 * Order of resolution:
 *   1. `row.s3_key` — persisted at upload time (all current uploads).
 *   2. `s3_sync_log` lookup on `order_id` + the ORIGINAL file name. Historical
 *      rows have no `s3_key`; uploads log `source_path` as the original file
 *      name, so we match on `file_name` and on the original-name form derived
 *      from the randomized storage path (`<ts>-<rand>.ext` → `<name>.ext`).
 *
 * The randomized storage path itself is deliberately NOT a candidate — it can
 * never match `source_path`, and matching on it was the bug that let the
 * cancellation / trailer-unsync cleanups silently skip every S3 delete.
 */
export async function resolveDocumentS3Keys(
  row: OrderDocKeyRow,
  orderUniqueId: string | null | undefined,
): Promise<string[]> {
  if (row.s3_key) return [row.s3_key];

  const filePath = row.image_path && !row.image_path.startsWith("http") ? row.image_path : null;
  const derivedName = filePath ? filePath.split("/").pop()?.replace(/^\d+-/, "") || "" : "";
  const candidates = Array.from(
    new Set([row.file_name, derivedName].filter(Boolean) as string[]),
  );
  if (!orderUniqueId || candidates.length === 0) return [];

  const { data: syncRows } = await supabase
    .from("s3_sync_log")
    .select("s3_key, source_path, status")
    .eq("order_id", orderUniqueId)
    .eq("status", "synced")
    .in("source_path", candidates);

  return Array.from(
    new Set(((syncRows ?? []) as any[]).map((r) => r.s3_key as string).filter(Boolean)),
  );
}

/**
 * Never fail silently: record an unresolved-key orphan in `s3_sync_log` so the
 * missing S3 delete is auditable instead of invisible.
 */
export async function logUnresolvedS3Key(params: {
  bucket: string;
  orderUniqueId: string | null | undefined;
  tenantId: string | null | undefined;
  sourcePath: string | null | undefined;
  context: string;
}): Promise<void> {
  try {
    await supabase.from("s3_sync_log").insert({
      order_id: params.orderUniqueId ?? null,
      tenant_id: params.tenantId ?? null,
      source_bucket: params.bucket,
      source_path: params.sourcePath ?? null,
      s3_key: "",
      status: "failed",
      error_code: "S3_KEY_UNRESOLVED",
      error_message: `Local document deleted but no S3 key could be resolved (${params.context})`,
    } as never);
  } catch (logErr) {
    console.warn("[order-doc-delete] Failed to log unresolved S3 key", logErr);
  }
}

/**
 * Single source of truth for deleting an order-attached BoL / Tender document.
 *
 * Sync-then-Persist: the NetSuite-facing S3 object(s) are deleted FIRST. If S3
 * deletion fails we throw and leave the local row intact, so the document stays
 * visible in ATMS and the operator can retry — rather than silently orphaning
 * the file in the bucket (the failure mode this helper was written to kill).
 *
 * Every previous copy of this logic lived inline in a viewer component; two of
 * them (driver views) skipped S3 entirely. Always call this instead.
 */
export async function deleteOrderDocument(params: {
  table: OrderDocTable;
  bucket: string;
  rowId: string;
  imagePath: string | null | undefined;
  orderUniqueId: string | null | undefined;
  tenantId: string | null | undefined;
  tenantSlug: string | null | undefined;
}): Promise<void> {
  const { table, bucket, rowId, imagePath, orderUniqueId, tenantId, tenantSlug } = params;

  const filePath = imagePath && !imagePath.startsWith("http") ? imagePath : null;

  // 1. Resolve the S3 key(s) for this document via the shared resolver.
  const { data: docRow } = await supabase
    .from(table)
    .select("s3_key, file_name")
    .eq("id", rowId)
    .maybeSingle();
  const storedFileName = ((docRow as any)?.file_name as string) ?? null;

  const s3Keys = await resolveDocumentS3Keys(
    {
      s3_key: (docRow as any)?.s3_key ?? null,
      file_name: storedFileName,
      image_path: filePath,
    },
    orderUniqueId,
  );

  // 1b. Never fail silently: if we cannot resolve a key, record the orphan so
  //     it is auditable instead of invisible, and warn the operator.
  if (s3Keys.length === 0) {
    console.warn(
      `[order-doc-delete] No S3 key resolved for ${table} row ${rowId} (order ${orderUniqueId ?? "?"}, path ${filePath ?? "?"}) — the S3 object may be orphaned.`,
    );
    await logUnresolvedS3Key({
      bucket,
      orderUniqueId,
      tenantId,
      sourcePath: storedFileName ?? filePath,
      context: `manual delete of ${table}`,
    });
  }

  // 2. Delete each S3 object via sync-bol-s3 (blocking).
  for (const key of s3Keys) {
    const { data, error } = await supabase.functions.invoke("sync-bol-s3", {
      body: {
        action: "delete",
        tenant_slug: tenantSlug,
        order_id: orderUniqueId,
        s3_key: key,
      },
    });
    if (error || (data && data.success === false)) {
      const detail = await extractSyncBolS3Error(error, data);
      throw new Error(`S3 delete failed for ${key}: ${formatSyncBolS3Error(detail)}`);
    }
  }

  // 3. Best-effort: remove the underlying storage object.
  if (filePath) {
    await supabase.storage.from(bucket).remove([filePath]).catch(() => undefined);
  }

  // 4. Delete the DB row (tenant-scoped when we know the tenant).
  let del = supabase.from(table).delete().eq("id", rowId);
  if (tenantId) del = del.eq("tenant_id", tenantId);
  const { error: dbErr } = await del;
  if (dbErr) throw dbErr;

  // 5. Best-effort: clean the associated document_extractions row.
  if (filePath && tenantId) {
    try {
      const fileName = filePath.split("/").pop()?.replace(/^\d+-/, "") || "";
      const orConditions = [`file_path.eq.${filePath}`];
      if (fileName) orConditions.push(`file_name.eq.${fileName}`);
      await supabase
        .from("document_extractions")
        .delete()
        .eq("tenant_id", tenantId)
        .or(orConditions.join(","));
    } catch (extractionErr) {
      console.warn("Failed to clean document_extractions row", extractionErr);
    }
  }
}

/**
 * Resolve the `{ unique_id, tenant_id, tenant_slug }` an S3 delete needs from a
 * bare order id. Driver-side viewers only carry the order UUID.
 */
export async function resolveOrderS3Context(orderId: string | null | undefined): Promise<{
  orderUniqueId: string | null;
  tenantId: string | null;
  tenantSlug: string | null;
}> {
  if (!orderId) return { orderUniqueId: null, tenantId: null, tenantSlug: null };
  const { data, error } = await supabase
    .from("orders")
    .select("unique_id, tenant_id, tenants(slug)")
    .eq("id", orderId)
    .maybeSingle();
  if (error || !data) {
    return { orderUniqueId: null, tenantId: null, tenantSlug: null };
  }
  const tenant = (data as any).tenants;
  return {
    orderUniqueId: (data as any).unique_id ?? null,
    tenantId: (data as any).tenant_id ?? null,
    tenantSlug: Array.isArray(tenant) ? (tenant[0]?.slug ?? null) : (tenant?.slug ?? null),
  };
}